import { AssetsLoading } from "@hg/assets-loader"
import { SafeAreaView } from "@hg/layouts"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"

import LoadingCar from "@/components/LoadingCar"
import LoadingTitle from "@/components/LoadingTitle"
import RecallEntry from "@/components/RecallEntry"
import StrokeText from "@/components/StrokeText"
import { useCheckLogin } from "@/hooks"
import { i18n } from "@/i18n"
import {
    GlobalStage,
    useGlobalStore,
    useSetGlobalStage,
    useTransitionStore,
} from "@/store"
import { collectStart } from "@/utils/event"

import styles from "./index.module.scss"

interface LoadingProps {
    onSuccess: () => void
}

const ProgressReporter = ({
    progress,
    onProgress,
}: {
    progress: number
    onProgress: (progress: number) => void
}) => {
    useEffect(() => {
        onProgress(progress)
    }, [onProgress, progress])

    return null
}

const Loading = ({ onSuccess }: LoadingProps) => {
    const resetLoadingDisplayProgress = useGlobalStore(
        (state) => state.resetLoadingDisplayProgress,
    )
    const setLoadingDisplayProgress = useGlobalStore(
        (state) => state.setLoadingDisplayProgress,
    )
    const [rawProgress, setRawProgress] = useState(0)
    const [displayProgress, setDisplayProgress] = useState(0)
    const [assetsReady, setAssetsReady] = useState(false)
    const rawProgressRef = useRef(0)
    const displayProgressRef = useRef(0)
    const assetsReadyRef = useRef(false)
    const successNotifiedRef = useRef(false)

    useEffect(() => {
        resetLoadingDisplayProgress()
        rawProgressRef.current = 0
        displayProgressRef.current = 0
        assetsReadyRef.current = false
        successNotifiedRef.current = false
    }, [resetLoadingDisplayProgress])

    useEffect(() => {
        rawProgressRef.current = rawProgress
    }, [rawProgress])

    useEffect(() => {
        assetsReadyRef.current = assetsReady
    }, [assetsReady])

    useEffect(() => {
        displayProgressRef.current = displayProgress
        setLoadingDisplayProgress(displayProgress)
    }, [displayProgress, setLoadingDisplayProgress])

    useEffect(() => {
        let rafId = 0
        let prevTime = performance.now()

        const tick = (now: number) => {
            const deltaSeconds = Math.min((now - prevTime) / 1000, 0.1)
            prevTime = now

            const currentProgress = displayProgressRef.current
            const targetProgress = Math.min(
                Math.max(
                    assetsReadyRef.current ? 1 : rawProgressRef.current,
                    currentProgress,
                ),
                1,
            )

            if (targetProgress > currentProgress) {
                const speed = currentProgress < 0.84 ? 0.36 : 0.22
                const nextProgress = Math.min(
                    currentProgress + speed * deltaSeconds,
                    targetProgress,
                    1,
                )
                const normalizedProgress =
                    1 - nextProgress < 0.001 ? 1 : nextProgress

                if (normalizedProgress !== currentProgress) {
                    displayProgressRef.current = normalizedProgress
                    setDisplayProgress(normalizedProgress)
                }
            }

            rafId = window.requestAnimationFrame(tick)
        }

        rafId = window.requestAnimationFrame(tick)
        return () => window.cancelAnimationFrame(rafId)
    }, [])

    useEffect(() => {
        if (!assetsReady || displayProgress < 1 || successNotifiedRef.current) {
            return
        }

        successNotifiedRef.current = true
        window.setTimeout(() => {
            onSuccess()
        }, 500)
    }, [assetsReady, displayProgress, onSuccess])

    return (
        <AssetsLoading
            render={(progress) => {
                const percentage = Math.min(displayProgress * 100, 100)
                const percentageValue = percentage.toFixed(3)
                return (
                    <>
                        <ProgressReporter
                            progress={progress}
                            onProgress={setRawProgress}
                        />
                        <div className={styles.progress}>
                            <div className={styles.wrapper}>
                                <div className={styles.bar}>
                                    <div
                                        className={styles.barInner}
                                        style={{
                                            clipPath: `polygon(0 0, ${percentageValue}% 0, ${percentageValue}% 100%, 0 100%)`,
                                        }}
                                    />
                                </div>
                                <div
                                    className={styles.car}
                                    style={{ left: `${percentageValue}%` }}
                                />
                            </div>
                        </div>
                    </>
                )
            }}
            onSuccess={() => setAssetsReady(true)}
        />
    )
}

export const LoadingPage = () => {
    const setGlobalStage = useSetGlobalStage()
    const setGameStartTs = useGlobalStore((state) => state.setGameStartTs)
    const fadeTransition = useTransitionStore((s) => s.fadeTransition)
    const shouldReduceMotion = useReducedMotion() ?? false
    const checkLogin = useCheckLogin()

    const [loaded, setLoaded] = useState(false)

    return (
        <div className={styles.page}>
            <SafeAreaView
                mode="margin"
                edges={["left"]}
                className={styles.logo}
            />
            <RecallEntry />
            <LoadingTitle />
            <LoadingCar />
            <div className={styles.startButtonWrapper}>
                <AnimatePresence mode="wait" initial={false}>
                    {loaded ? (
                        <motion.button
                            key="start-button"
                            type="button"
                            className={styles.startButton}
                            initial={{ opacity: 0, y: 16, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 16, scale: 0.96 }}
                            whileHover={
                                shouldReduceMotion
                                    ? undefined
                                    : {
                                          y: -3,
                                          scaleX: 1.02,
                                          scaleY: 1.04,
                                      }
                            }
                            whileTap={
                                shouldReduceMotion
                                    ? undefined
                                    : {
                                          y: 2,
                                          scaleX: 1.04,
                                          scaleY: 0.94,
                                      }
                            }
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                            onClick={() => {
                                if (checkLogin()) {
                                    const ts = Math.floor(Date.now() / 1000)
                                    setGameStartTs(ts)
                                    collectStart(ts)
                                    fadeTransition(() =>
                                        setGlobalStage(GlobalStage.MAIN),
                                    )
                                }
                            }}
                        >
                            <StrokeText
                                className={styles.btnText}
                                text={i18n.get("loading.button")}
                                fontFamily="source-han-sans-japanese"
                                fontWeight={700}
                                fontSize={48}
                                fillColor="#fff"
                                borderWidth={4}
                                borderColor="#564426"
                                offsetY={0}
                                padding={4}
                                baselineAligned
                            />
                        </motion.button>
                    ) : (
                        <motion.div
                            key="loading"
                            initial={{ opacity: 1, y: 0, scale: 1 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 16, scale: 0.98 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                            <Loading onSuccess={() => setLoaded(true)} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}
